import { Component, OnInit } from '@angular/core';
import {DisplayTypes} from "@catui/components/panel/types";
import {GgwProfile} from "@catmodels/ggw-profiles/types";
import {Project} from "@catcore/types/api-v2";
import {Subscription} from "rxjs";
import {StyleType} from "@catui/types";
import {ProjectService} from "@catcore/services";
import {BreadcrumbService} from "@catmodules/widgets/breadcrumb/services/breadcrumb.service";
import {ModalService} from "@catmodules/widgets/slide-in-modal/services/modal.service";
import {GgwProfilePolicyService} from "@catmodels/ggw-profiles/services/ggw-profile-policy.service";
import {GenericResourceService} from "@catfeatures/services/generic-resource.service";
import {isNullOrUndefined} from "util";

@Component({
  selector: 'catapp-ggw-device-access',
  templateUrl: './ggw-device-access.component.html',
  styleUrls: ['./ggw-device-access.component.scss']
})
export class GgwDeviceAccessComponent implements OnInit {

  tableItems = [];
  dataTableInitialized: boolean;
  project: Project;

  ggwDevices = [];
  ggwProfiles = [];
  ggwProfilePolicies = [];
  defaultPolicies = [];
  guiZones = [];
  zoneAssignments = [];
  zonePolicies = [];

  panelDisplayType = DisplayTypes;

  cols = [];
  staticCols = [
    { field: 'parentZoneLabel', header: 'Native Zone', parentShortName: 'GGW Device', static: true },
    { field: 'description', header: 'Device', parentShortName: 'GGW Device', static: true },
    { field: 'resolvedConfigName', header: 'Config Name', parentShortName: 'GGW Device', static: true },
    { field: 'class', header: 'Class', parentShortName: 'GGW Device', static: true },
    { field: 'roleLabel', header: 'Role', parentShortName: 'GGW Device', static: true },
  ];

  groupCols = [];
  staticGroupCols = [
    { shortName: 'GGW Device', count: this.staticCols.length, children: this.staticCols }
  ];

  projectSubscription: Subscription;
  ggwDeviceSubscription: Subscription;
  ggwProfileSubscription: Subscription;
  ggwProfilePolicySubscription: Subscription;
  ggwProfileDefaultDevicePolicySubscr: Subscription;
  zoneAssignmentSubscription: Subscription;
  zonePoliciesSubscription: Subscription;

  defaultPoliciesReady: boolean;
  profilesReady: boolean;
  policiesReady: boolean;
  devicesReady: boolean;
  zoneAssignmentsReady: boolean;
  zonePoliciesReady: boolean;

  styleType = StyleType;

  profileOptions = [];
  zoneOptions = [];

  constructor(private projectService: ProjectService,
              private breadcrumbService: BreadcrumbService,
              private modalService: ModalService,
              private ggwProfilePolicyService: GgwProfilePolicyService,
              private resourceService: GenericResourceService) { }

  ngOnInit() {
    // first get the current project
    this.projectSubscription = this.projectService.getCurrentProject()
      .subscribe((project) => {
        this.project = project;
        const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
        if (isActiveProject) {
          this.dataTableInitialized = false;
          this.defaultPoliciesReady = false;
          this.profilesReady = false;
          this.policiesReady = false;
          this.devicesReady = false;
          this.zoneAssignmentsReady = false;
          this.zonePoliciesReady = false;

          this.guiZones = this.projectService
            .extractGuiZonesFrom(this.project)
            .filter((guiZone) => { return guiZone.type !== 'zoneGroup' });
          this.zoneOptions = [];
          this.guiZones.forEach((zone) => {
            this.zoneOptions.push({
              label: zone.name,
              value: zone
            })
          });

          this.ggwProfileSubscription = this.projectService.getGgwProfiles(this.project)
            .subscribe((ggwProfiles: any) => {
              this.ggwProfiles = ggwProfiles;
              this.profileOptions = [];
              this.ggwProfiles.forEach((profile) => {
                this.profileOptions.push({
                  label: profile.label,
                  value: profile
                })
              });
              this.profilesReady = true;
              this.updateDataTable();
            });

          this.ggwDeviceSubscription = this.projectService.getAllGgwDevices(this.project, true)
            .subscribe((ggwDevices: any) => {
              this.ggwDevices = ggwDevices;
              this.devicesReady = true;
              this.updateDataTable();
            });

          this.zoneAssignmentSubscription = this.projectService.getGgwDevZoneAssignment(this.project)
            .subscribe((zoneAssignments) => {
              this.zoneAssignments = zoneAssignments;
              this.zoneAssignmentsReady = true;
              this.updateDataTable();
            });

          this.zonePoliciesSubscription = this.projectService.getGgwProfileZonePolicies(project)
            .subscribe((zonePolicies) => {
              this.zonePolicies = zonePolicies;
              this.zonePoliciesReady = true;
              this.updateDataTable();
            });

          this.ggwProfileDefaultDevicePolicySubscr = this.projectService.getGgwProfileDefaultDevicePolicies(this.project)
            .subscribe((defaultPolicies) => {
              this.defaultPolicies = defaultPolicies;
              this.defaultPoliciesReady = true;
              this.updateDataTable();
            });

          this.ggwProfilePolicySubscription = this.projectService.getAllGgwProfilePolicies(this.project)
            .subscribe((ggwProfilePolicies: any) => {
              this.ggwProfilePolicies = ggwProfilePolicies.filter((policy) => {
                return policy._links.hasOwnProperty('ggw_device');
              });
              this.policiesReady = true;
              this.updateDataTable();
            });

        }
      });

    this.breadcrumbService.update([{label: 'Device Access', value: 'ggwDeviceAccess' }]);
  }

  ngOnDestroy() {
    this.ggwDeviceSubscription.unsubscribe();
    this.projectSubscription.unsubscribe();
    this.ggwProfilePolicySubscription.unsubscribe();
    this.ggwProfileSubscription.unsubscribe();
    this.zoneAssignmentSubscription.unsubscribe();
    this.zonePoliciesSubscription.unsubscribe();
    this.ggwProfileDefaultDevicePolicySubscr.unsubscribe();
  }

  /**
   * Returns true if the given column name belongs to a static column.
   *
   * @param colName
   */
  isStaticCol(colName) {
    return this.staticCols.find((c) => c.field === colName);
  }


  /**
   * Returns true if the zone is allowed for this ggw profile.
   *
   * @param rowItem
   * @param colField
   */
  isActive(rowItem, colField) {
    return this.isActiveStatus(this.getStatus(rowItem, colField));
  }

  isDeny(rowItem, colField) {
    return this.isDenyStatus(this.getStatus(rowItem, colField));
  }

  isDenyPolicy(rowItem, colField) {
    return this.getCurrentPolicy(rowItem, colField) === 'deny';
  }

  isActivePolicy(rowItem, colField) {
    return this.getCurrentPolicy(rowItem, colField) === 'allow' || this.getCurrentPolicy(rowItem, colField) === 'allow-recursive';
  }

  isActiveRecursivePolicy(rowItem, colField) {
    return this.getCurrentPolicy(rowItem, colField) === 'allow-recursive';
  }

  isActiveEnabled(rowItem, colField) {
    let status = this.getStatus(rowItem, colField);
    return this.isEnabledStatus(status) && this.isActiveStatus(status);
  }

  isActiveRecursive(rowItem, colField) {
    return this.isActiveRecursiveStatus(this.getStatus(rowItem, colField));
  }

  isActiveDisabled(rowItem, colField) {
    return this.getStatus(rowItem, colField) === 'active-disabled';
  }

  isInactive(rowItem, colField) {
    return this.isInactiveStatus(this.getStatus(rowItem, colField));
  }

  isEnabledStatus(status) {
    return status === 'active-enabled'
      || status === 'inactive-enabled'
      || status === 'active-enabled-recursive'
      || status === 'deny-enabled';
  }

  isActiveStatus(status) {
    return status === 'active-disabled'
      || status === 'active-enabled'
      || status === 'active-enabled-recursive';
  }

  isInactiveStatus(status) {
    return status === 'inactive-enabled'
      || status === 'deny-enabled'
      || status === 'deny-disabled';
  }

  isDenyStatus(status) {
    return status === 'deny-enabled'
      || status === 'deny-disabled';
  }

  isActiveRecursiveStatus(status) {
    return status === 'active-enabled-recursive';
  }

  isAllowPolicy(policy) {
    return policy === 'allow-recursive' || policy === 'allow';
  }

  isRecursivePolicy(policy) {
    return policy === 'allow-recursive' || policy === 'deny-recursive';
  }

  /**
   * Returns true if the default policy differs from the current policy.
   *
   * @param rowItem
   * @param colField
   */
  hasDifferentDefault(rowItem, colField) {
    const currentPolicy = this.getCurrentPolicy(rowItem, colField);
    const defaultPolicy = rowItem.defaultPolicies.hasOwnProperty(colField) ? rowItem.defaultPolicies[colField] : 'none';
    return currentPolicy !== defaultPolicy;
  }

  /**
   * Returns the status for a given cell, identified by rowItem and column field.
   * For seats, the status of the parent zone is checked for a recursive policy.
   *
   * @param rowItem
   * @param colField
   */
  getStatus(rowItem, colField) {
    if (this.isStaticCol(colField)) {
      return null;
    }

    const profile = this.findProfile(colField);
    if (profile.default_policy === 'allow') {
      if (rowItem.policies[colField] && rowItem.policies[colField].policy === 'deny') {
        return 'deny-enabled';
      }

      return 'active-disabled';
    }

    if (rowItem.inheritedPolicies.hasOwnProperty(colField)) {
      if (rowItem.inheritedPolicies[colField].value === 'allow-recursive') {
        if (rowItem.policies[colField] && rowItem.policies[colField].policy === 'deny') {
          return 'deny-enabled';
        }

        return 'active-disabled';
      }
    }

    if (rowItem.policies[colField] && rowItem.policies[colField].policy  === 'allow') {
      return 'active-enabled';
    }

    if (rowItem.policies[colField] && rowItem.policies[colField].policy === 'allow-recursive') {
      return 'active-enabled-recursive';
    }

    if (rowItem.policies[colField] && rowItem.policies[colField].policy === 'none') {
      return 'inactive-enabled';
    }

    if (rowItem.policies[colField] && rowItem.policies[colField].policy === 'deny') {
      return 'deny-disabled';
    }

    if (rowItem.defaultPolicies[colField] === 'allow') {
      return 'active-enabled';
    }

    if (rowItem.defaultPolicies[colField] === 'deny') {
      return 'active-enabled';
    }

    if (rowItem.defaultPolicies[colField] === 'allow-recursive') {
      return 'active-enabled-recursive';
    }

    return 'inactive-enabled';
  }

  /**
   * Returns true if the edit button should be displayed for a cell.
   *
   * @param rowItem
   * @param colField
   */
  showEditButton(rowItem, colField) {
    return colField === 'name';
  }

  /**
   * Returns the tooltip title for a cell.
   *
   * @param rowItem
   * @param col
   */
  getTitle(rowItem, col) {
    if (col.static) {
      return col.header;
    }

    let title = '';

    const inheritedPolicy = rowItem.inheritedPolicies[col.field];
    if (inheritedPolicy) {
      title += inheritedPolicy.label;
    }

    if (this.isEnabledStatus(this.getStatus(rowItem, col.field))) {
      title += (title === '' ? '' : '. ');
      title += 'Click to toggle ' + col.header;
    }

    if (this.hasDifferentDefault(rowItem, col.field)) {
      title += (title === '' ? '' : '. ');
      title += 'Default: ' + this.getDefaultPolicy(rowItem, col.field);
    }

    return title;
  }

  /**
   * Handles a cell click. If a data cell has been clicked, the policy of the given zone/profile combination
   * is toggled through none -> active -> active recursive -> deny -> none.
   *
   * Updating the policy means to either add, update or delete a GgwProfilePolicy. The operation depends
   * on the default policy: If the desired policy equals the default policy, no GgwProfilePolicy is needed.
   *
   * @param rowItem
   * @param col
   */
  cellClick(rowItem, col) {
    if (col.static) {
      return;
    }

    const inheritedPolicy = this.getInheritedPolicy(rowItem, col.field);

    const curPolicy = this.getCurrentPolicy(rowItem, col.field);
    const nextPolicy = this.getNextTogglePolicy(curPolicy, inheritedPolicy);

    // If next policy equals default - just remove item
    const defaultPolicy = this.getDefaultPolicy(rowItem, col.field);
    if (defaultPolicy === nextPolicy) {
      this.deleteItem(rowItem.policies[col.field]);
      return;
    }

    // If item exists - update it
    if (rowItem.policies.hasOwnProperty(col.field)) {
      this.updateItem(rowItem.policies[col.field], nextPolicy);
      return;
    }

    // Else, add new item
    this.addItem(rowItem.ggwDevice._links.self.href, col.field, nextPolicy);
  }

  /**
   * Finds a gui zone by uri.
   *
   * @param guiZoneUri
   */
  private findGuiZone(guiZoneUri) {
    return this.guiZones.find((gz) => { return gz._links.self.href === guiZoneUri });
  }

  /**
   * Finds a ggw profile by uri.
   *
   * @param ggwProfileUri
   */
  private findProfile(ggwProfileUri) {
    return this.ggwProfiles.find((p) => { return p._links.self.href === ggwProfileUri });
  }

  /**
   * Returns current policy, which can be determined by an explicit ggw profile policy entity (1st prio)
   * or by an existing default policy.
   *
   * @param rowItem
   * @param colField
   */
  private getCurrentPolicy(rowItem, colField) {

    if (rowItem.policies.hasOwnProperty(colField)) {
      return rowItem.policies[colField].policy;
    }

    if (rowItem.defaultPolicies.hasOwnProperty(colField)) {
      return rowItem.defaultPolicies[colField];
    }

    return 'none';
  }

  /**
   * Returns the inherited policy for a cell (default 'none').
   *
   * @param rowItem
   * @param colField
   */
  private getInheritedPolicy(rowItem, colField) {

    const profile = this.findProfile(colField);
    if (profile.default_policy === 'allow') {
      return 'allow';
    }

    if (rowItem.inheritedPolicies.hasOwnProperty(colField)) {
      if (rowItem.inheritedPolicies[colField].value === 'allow-recursive') {
        return 'allow';
      }
    }

    return 'none';
  }

  /**
   * Returns the next policy for a toggle operation.
   *
   * @param policy
   */
  private getNextTogglePolicy(policy, inheritedPolicy) {
    const next = {
      'none': {
        // If no policy is inherited, toggle through allow policies
        'none': 'allow',
        'allow': 'allow-recursive',
        'allow-recursive': 'none',
        'deny': 'none', // Only for completeness to avoid invalid initial state
      },
      'allow': {
        // If an allow policy is inherited, toggle through deny policy
        'none': 'deny',
        'deny': 'none',
        'allow': 'none', // Only for completeness to avoid invalid initial state
        'allow-recursive': 'none', // Only for completeness to avoid invalid initial state
      }
    };

    return next[inheritedPolicy][policy];
  }

  /**
   * Adds a new ggw profile policy.
   *
   * @param ggwDeviceUri
   * @param ggwProfileUri
   * @param policy
   */
  private addItem(ggwDeviceUri, ggwProfileUri, policy) {
    this.ggwProfilePolicyService.add({
      ggw_profile: ggwProfileUri,
      ggw_device: ggwDeviceUri,
      policy: policy
    })
      .subscribe((result) => {
        this.ggwProfilePolicies.push(result);
        this.updateTableItem(ggwDeviceUri, ggwProfileUri, result);
      });
  }

  /**
   * Updates an existing ggw profile policy.
   *
   * @param item
   * @param policy
   */
  private updateItem(item, policy) {
    const subscr = this.resourceService.findServiceForResource(item._links.self.href)
      .save({ policy: policy })
      .finally(() => subscr.unsubscribe())
      .subscribe((result) => {
        this.updateTableItem(item._links.ggw_device.href, item._links.ggw_profile.href, result);
      });
  }

  /**
   * Deletes a given ggw profile policy.
   *
   * @param item the ggw profile policy
   */
  private deleteItem(item) {
    this.ggwProfilePolicyService.delete(item)
      .subscribe((result) => {
        let index = this.ggwProfilePolicies.indexOf(item);
        this.tableItems
          .filter((tableItem) => {return item._links.ggw_device.href === tableItem.ggwDevice._links.self.href})
          .forEach((tableItem) => {
            delete tableItem.policies[item._links.ggw_profile.href];
          });
        this.ggwProfilePolicies.splice(index, 1);
      });
  }

  private updateDataTable() {
    if (!this.devicesReady
      || !this.profilesReady
      || !this.zoneAssignmentsReady
      || !this.zonePoliciesReady
      || !this.defaultPoliciesReady
      || !this.policiesReady) {
      return;
    }

    this.updateCols();

    this.tableItems = [];
    this.ggwDevices.forEach((ggwDevice) => {
      this.addGgwDevice(ggwDevice);
    })
  }

  private updateCols() {
    let dataCols = [];
    this.ggwProfiles.forEach((profile) => {
      const col = {
        field: profile._links.self.href,
        header: profile.label,
        parentShortName: profile.type,
        static: false
      };
      dataCols.push(col);
    });
    dataCols.sort((a, b) => {
      let parentZoneCmp = a.parentShortName.toUpperCase().localeCompare(b.parentShortName.toUpperCase());
      if (parentZoneCmp !== 0) {
        return parentZoneCmp;
      }
      return a.header.toUpperCase().localeCompare(b.header.toUpperCase());
    });

    let colspanCount = dataCols.reduce((p, c) => {
      let name = c.parentShortName;
      if (!p.hasOwnProperty(name)) {
        p[name] = {
          count: 0,
          children: []
        };
      }
      p[name].count++;
      p[name].children.push(c);
      return p;
    }, {});

    let dataColspanCount = Object.keys(colspanCount).map(k => {
      return {
        shortName: k,
        count: colspanCount[k].count,
        children: colspanCount[k].children
      };
    });

    this.groupCols = [
      ...this.staticGroupCols,
      ...dataColspanCount
    ];
    this.cols = [
      ...this.staticCols,
      ...dataCols
    ];
  }

  private updateTableItem(ggwDeviceUri, ggwProfileUri, profilePolicy) {
    this.tableItems
      .filter((item) => {return item.ggwDevice._links.self.href === ggwDeviceUri})
      .forEach((item) => {
        item.policies[ggwProfileUri] = profilePolicy;
      });
  }


  private getParentShortName(guiZone) {
    if (guiZone._embedded && guiZone._embedded.zone_parent) {
      return guiZone._embedded.zone_parent.short_name;
    } else {
      return guiZone.short_name;
    }
  }

  private addGgwDevice(ggwDevice: any) {

    const zoneIris = this.zoneAssignments.hasOwnProperty(ggwDevice._links.self.href) ? this.zoneAssignments[ggwDevice._links.self.href] : [];

    const item = {
      'parentZoneLabel': ggwDevice._links.gui_zone.label,
      'ggwDevice': ggwDevice,
      'description': ggwDevice.resolved_description,
      'configName': ggwDevice.config_name,
      'class': ggwDevice.class,
      'roleLabel': ggwDevice.role_label,
      'resolvedConfigName': ggwDevice.resolved_config_name,
      'zoneIris': zoneIris,
      'inheritedPolicies': this.getInheritedPolicies(ggwDevice, zoneIris),
      'defaultPolicies': this.getDefaultPolicies(ggwDevice),
      'policies': this.getPolicies(ggwDevice)
    };

    this.tableItems = [...this.tableItems, item];
  }

  private getPolicies(ggwDevice) {
      const policies = {};
      this.ggwProfilePolicies
        .filter((profilePolicy) => profilePolicy._links.ggw_device.href === ggwDevice._links.self.href)
        .forEach((profilePolicy) => {
            policies[profilePolicy._links.ggw_profile.href] = profilePolicy;
        });
      return policies;
  }

  private getInheritedPolicies(ggwDevice, zoneIris) {
    const inheritedPolicies = {};
    this.ggwProfiles.forEach((profile) => {
      zoneIris.forEach((zoneIri) => {
        if (this.zonePolicies[profile._links.self.href].hasOwnProperty(zoneIri)
          && this.isRecursivePolicy(this.zonePolicies[profile._links.self.href][zoneIri])) {
          inheritedPolicies[profile._links.self.href] = {
            value: this.zonePolicies[profile._links.self.href][zoneIri],
            label: 'Inherited from ' + this.findGuiZone(zoneIri).name
          };
          return;
        }
        // For seats, also check zone parent
        const zone = this.findGuiZone(zoneIri);
        if (zone.type === 'seat' && zone._links.zone_parent) {
          const zoneParentIri = zone._links.zone_parent.href;
          if (this.zonePolicies[profile._links.self.href].hasOwnProperty(zoneParentIri)
            && this.isRecursivePolicy(this.zonePolicies[profile._links.self.href][zoneParentIri])) {
            inheritedPolicies[profile._links.self.href] = {
              value: this.zonePolicies[profile._links.self.href][zoneParentIri],
              label: 'Inherited from ' + this.findGuiZone(zoneParentIri).name
            };
            return;
          }
        }
      });
    });
    return inheritedPolicies;
  }

  private getDefaultPolicies(ggwDevice) {
    if (this.defaultPolicies.hasOwnProperty(ggwDevice._links.self.href)) {
      return this.defaultPolicies[ggwDevice._links.self.href];
    } else {
      return {};
    }
  }

  private getDefaultPolicy(rowItem, colField) {
    if (rowItem.defaultPolicies.hasOwnProperty(colField)) {
      return rowItem.defaultPolicies[colField];
    }

    return 'none';
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/ggw-profiles/components/ggw-device-access/ggw-device-access.component.ts